import { useAuth0 } from "@auth0/auth0-react";
import { Login, LoginUserListItemDto } from "features/auth/types";
import { fetchCurrentLoginUser, fetchLoginUsers } from "../utils/federatedApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "modules/notifications/hooks";
import { useContext, useEffect } from "react";
import { AuthTokenContext } from "./authContext";
import { SelectedOrgContext } from "./orgContext";
import { clearTableStateStorage } from "lib/storage/clearTableStateStorage";
import { saveToken, deleteToken, deleteUserData } from "../utils/storage";
import { getLegacyOrgHint } from "../utils/legacyOrgHint";
import { clearStoredCacheIdentity } from "lib/react-query/cacheIdentity";
import { persister } from "lib/react-query/indexedDB";
import { DOMAIN } from "config";

const userKey = "federated-current-user";
const loginUsersKey = "federated-login-users";

// sessionStorage key carrying the deep link a logged-out user hit, across the Auth0 redirect
// round trip. Written by useLogin before the redirect; consumed once by AuthLoaderFederated
// (inside the router) after authentication. See the block comment in useLogin for why this is
// sessionStorage and not appState.returnTo.
export const RETURN_TO_SESSION_KEY = "delogue-federated-return-to";

export const useAuthState = () => useContext(AuthTokenContext);
export const useSelectedOrg = () => useContext(SelectedOrgContext);

export const useLoginUsers = () => {
  const { isAuthenticated, isLoading, token } = useAuthState();
  const enabled = !isLoading && isAuthenticated && !!token;
  return useQuery<LoginUserListItemDto[], Error>({
    queryKey: [loginUsersKey],
    queryFn: async () => await fetchLoginUsers(token ?? ""),
    enabled,
  });
};

export const useUser = () => {
  const { isAuthenticated, isLoading } = useAuthState();
  const { selectedOrgAuth0Id, orgToken, isImpersonating } = useSelectedOrg();
  // DL-64690: an impersonated session has no selected org by design (the delegated token is
  // already scoped to the customer's org), so it must satisfy the org gate on its own — otherwise
  // this query stays disabled, isPending never clears, and AuthLoaderFederated spins forever.
  const hasOrgContext = !!selectedOrgAuth0Id || isImpersonating;
  const enabled = !isLoading && isAuthenticated && hasOrgContext && !!orgToken;
  const query = useQuery<Login, Error>({
    // Org context MUST be part of the key (tanstack-hooks.md #3/#7): with a stale key,
    // TanStack Query's 24h staleTime means a legacy-side org switch (DL-61837, R6) never
    // triggers a refetch — selectedOrgAuth0Id/orgToken flip to the new org but `enabled`
    // stays true and the key is unchanged, so the D2 UI keeps rendering the previous org's
    // cached user/data indefinitely (Testmo 2507007 step 2).
    queryKey: [userKey, isImpersonating ? "impersonation" : selectedOrgAuth0Id],
    queryFn: async () => await fetchCurrentLoginUser(orgToken ?? ""),
    enabled,
    // it can change out from under an idle tab (expiry or the user clearing the cookie).
    // Refetching this one query on focus is what lets CacheIdentityGuard notice;
    // the global default is `false` and the 24h staleTime would otherwise keep serving
    // the previous user indefinitely. 30s stale time is a compromise to avoid spamming it
    refetchOnWindowFocus: true,
    staleTime: 30000,
  });

  // DL-66562: mirror the context into localStorage. userFn()'s saveToken() is unreachable
  // under USE_FEDERATED_AUTH, so `getUserData().loginContext` stayed null and every guard
  // reading it (taskboard delete/copy, table bulk actions, Segment) silently no-opped.
  //
  // Deliberately an effect on the observed data, not a side effect inside queryFn: the query
  // key is per-org but the localStorage key is not, so an org A fetch still in flight during a
  // switch to B would resolve later and write A over B (Greptile catch). Only the org this
  // hook is currently rendering ever reaches here — a stale fetch lands in its own inactive
  // cache entry, which no observer reads. A resolved-but-null login means the server has no
  // user for this token, so clear rather than keep stale context; a failed fetch leaves the
  // mirror untouched instead, so one dropped request can't disable the guarded actions.
  const { isSuccess, data } = query;
  useEffect(() => {
    if (!isSuccess) return;
    if (data?.loginContext) {
      saveToken(data.loginContext);
    } else {
      deleteToken();
    }
  }, [isSuccess, data]);

  return query;
};

export const useLogout = (): (() => Promise<void>) => {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  const { dismissNotification } = useNotification();
  const { clearSelectedOrg, beginLogout } = useSelectedOrg();

  return async () => {
    // Must come first: clearSelectedOrg() below drops the selected org while the
    // /betalogin/logout round trip is still in flight, and without the logging-out flag the
    // shell renders the account selector for those seconds (Slack report, DL-61837). The flag
    // keeps AuthLoaderFederated on a plain loader until the top-level navigation tears us down.
    beginLogout();
    queryClient.removeQueries({ queryKey: [userKey] });
    queryClient.removeQueries({ queryKey: [loginUsersKey] });
    dismissNotification();
    clearTableStateStorage();
    deleteUserData();
    clearSelectedOrg();

    // clear tanstack cache from memory and indexedDB
    queryClient.clear();
    try {
      await persister.removeClient();
      // Only forget the identity once the snapshot is genuinely gone
      clearStoredCacheIdentity();
    } catch {
      // Best effort; the in-memory clear above already happened, so nothing stale is on screen.
    }

    // DL-61835 (R5/X1): calling only the Auth0 SDK's logout() killed the Auth0 session (A) but
    // never touched the legacy Forms cookie, designerOrgInfoV2/supplierPortalInfo, or the
    // LoggedInUserDetail/principal cache rows — so a subsequent legacy/classic-embed page load
    // stayed logged in as the old user. /betalogin/logout clears all of those server-side and,
    // once Auth0 is enabled, returns the /v2/logout URL that finishes killing the shared Auth0
    // session; navigate there top-level (not fetch) so the browser actually carries the
    // Set-Cookie clearing headers and completes the redirect.
    //
    // If that request fails or returns a malformed body, falling back to just the SPA origin
    // would leave the Auth0 SSO cookie alive — the very next silent prompt=none round trip would
    // sign the same user straight back in. Fall back to the Auth0 SDK's own logout() instead,
    // which still terminates store A even though the legacy-side cleanup above didn't run
    // (Greptile catch on this PR).
    let logoutUrl: string | null = null;
    try {
      const response = await fetch(`${DOMAIN}/betalogin/logout`, {
        method: "GET",
        credentials: "include",
      });
      const data = await response.json();
      if (data?.LogoutUrl) {
        logoutUrl = data.LogoutUrl;
      }
    } catch {
      // Request failed, or wasn't the federated JSON shape — fall back to the SDK logout below.
    }

    if (logoutUrl) {
      window.location.assign(logoutUrl);
    } else {
      await logout({ logoutParams: { returnTo: window.location.origin } });
    }
  };
};

export const useLogin = (): (() => Promise<void>) => {
  const { loginWithRedirect, error } = useAuth0();
  const queryClient = useQueryClient();
  const { notify, dismissNotification } = useNotification();
  const { clearSelectedOrg } = useSelectedOrg();

  return async () => {
    queryClient.removeQueries({ queryKey: [userKey] });
    queryClient.removeQueries({ queryKey: [loginUsersKey] });
    dismissNotification();
    if (error) notify.error({ id: "error", message: error?.message });
    deleteUserData();
    clearSelectedOrg();

    // Preserve the deep link the logged-out user actually hit so the post-login redirect returns
    // them THERE, not the default landing route. Concretely: a supplier admin who clicks the
    // "review merge request" link in their email (/supplier/merge?mergeRequestId=…) is bounced
    // through Auth0 while logged out; without carrying the path they land on /suppliers and can
    // never find the review screen (they don't know the URL).
    //
    // Stashed in sessionStorage (survives the Auth0 redirect round trip within the same tab) rather
    // than passed as appState.returnTo: AuthProviderFederated sits above <RouterProvider> and cannot
    // navigate without importing the router, which the architecture gate rejects as a circular
    // dependency. AuthLoaderFederated — which lives inside the router — reads this key and navigates
    // via useNavigate once authenticated. Not setting appState lets the SDK's default
    // onRedirectCallback replace the URL with "/" (stripping ?code/&state) so the router starts clean
    // and the guarded navigate below always fires for a real deep link.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    sessionStorage.setItem(RETURN_TO_SESSION_KEY, returnTo);

    // DL-61835 (QA 2507004): when legacy already resolved an org (it appends `_auth0org` to the
    // "Try Delogue 2.0" link, mirroring the iframe's D2 -> legacy handoff), pass it straight into
    // the interactive redirect. Without it, Auth0 (the D2 client requires an org per request) shows
    // its own hosted org picker even though legacy already knows which org this session is on.
    const legacyOrgHint = getLegacyOrgHint();
    await loginWithRedirect({
      ...(legacyOrgHint
        ? { authorizationParams: { organization: legacyOrgHint } }
        : {}),
    });
  };
};
